import React, { useState, useEffect } from 'react';
import OverdueOrdersTab from '../components/OverdueOrdersTab';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { SalesPlanButton, SalesPlanModal, SalesPlanTab } from '../components/SalesPlanMerch';
import { TNAContent } from './TNA';
import { SampleBulletinTab } from '../components/SampleBulletin';
import { KpiCard } from '../ui';
import { reportError } from '../utils/reportError';

const MILESTONE_LABELS = {
  fit_sample: 'Fit Sample',
  lab_dip: 'Lab Dip',
  pp_sample: 'PP Sample',
  fpt: 'FPT',
  gpt: 'GPT',
  yarn: 'Yarn',
  knitting: 'Knitting',
  dyeing: 'Dyeing',
  finishing: 'Finishing',
};

const BUYER_MILESTONES = ['fit_sample', 'lab_dip', 'pp_sample', 'fpt', 'gpt'];
const FABRIC_MILESTONES = ['yarn', 'knitting', 'dyeing', 'finishing'];

export default function Merchandising() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [oeGpos, setOeGpos] = useState([]);
  const [oeFrs, setOeFrs] = useState([]);
  const [oePos, setOePos] = useState([]);
  const [oeEstimates, setOeEstimates] = useState([]);
  const [riskFlags, setRiskFlags] = useState({});   // N1: gpo_no → risk state
  const [loading, setLoading] = useState(true);
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [showEditOrder, setShowEditOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [milestoneData, setMilestoneData] = useState({});
  // The order whose milestones were actually READ. Saving writes all nine types, so a save over a
  // record that failed to load would reset every status to 'pending' (dates survive the route's
  // COALESCE, statuses do not). The tracker opens, and saves, only for the order loaded here.
  const [milestonesLoadedFor, setMilestonesLoadedFor] = useState(null);
  const [milestoneLoadErr, setMilestoneLoadErr] = useState('');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [mTab, setMTab] = useState('orders');          // orders | plans
  const [salesPlans, setSalesPlans] = useState([]);
  const [planOrder, setPlanOrder] = useState(null);    // order whose plan modal is open
  const loadPlans = () => api.get('/finance/sales-plan?mine=1').then(r => setSalesPlans(r.data.data || [])).catch(() => {});
  useEffect(() => { loadPlans(); }, []);
  const planByGpo = {}; salesPlans.forEach(p => { planByGpo[p.gpo_no] = p; });
  // Cost/pc for margin: match the order's style to a cost estimation.
  const costPerPcFor = (order) => {
    const ce = oeEstimates.find(e => e.style_no && e.style_no === order.style_no);
    return ce ? Number(ce.production_cost) || 0 : 0;
  };
  const [showAll, setShowAll] = useState(false);
  const [filterBuyer, setFilterBuyer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [tab, setTab] = useState('orders');

  useEffect(() => { fetchData(); }, []);

  // Orders is paginated server-side (company-scoped). Pull every page so the
  // client-side search/buyer/status filters see the full set — older orders
  // (incl. imported running orders) are no longer cut off at the first 50.
  const fetchAllOrders = async () => {
    const PAGE = 200;
    const first = await api.get('/orders', { params: { page: 1, limit: PAGE } });
    let all = first.data.data || [];
    const total = first.data.meta?.total ?? all.length;
    let page = 2;
    while (all.length < total) {
      const r = await api.get('/orders', { params: { page, limit: PAGE } });
      const rows = r.data.data || [];
      if (!rows.length) break;
      all = all.concat(rows);
      page++;
    }
    return all;
  };

  const fetchData = async () => {
    try {
      const [ordRes, buyRes, gpoRes, frsRes, poRes, estRes] = await Promise.all([
        fetchAllOrders(),
        api.get('/buyers'),
        api.get('/oe/gpos').catch(() => ({ data: { data: [] } })),
        api.get('/oe/frs').catch(() => ({ data: { data: [] } })),
        api.get('/procurement/pos').catch(() => ({ data: { data: [] } })),
        api.get('/oe/estimates').catch(() => ({ data: { data: [] } }))
      ]);
      api.get('/risk/flags').then(r => setRiskFlags(r.data.data || {})).catch(() => {});
      setOrders(ordRes || []);
      setBuyers(buyRes.data.data || []);
      setOeGpos(gpoRes.data.data || []);
      setOeFrs(frsRes.data.data || []);
      setOePos(poRes.data.data || []);
      setOeEstimates(estRes.data.data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };


  const handleAddBuyer = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post('/buyers', form);
      setShowAddBuyer(false); setForm({});
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add buyer');
    } finally { setSaving(false); }
  };

  const handleUpdateStatus = async (orderId, current_status) => {
    try {
      await api.patch(`/orders/${orderId}`, { current_status });
      fetchData();
    } catch (err) { console.error(err); }
  };

const handleSaveMilestone = async (e) => {
    e.preventDefault();
    if (!selectedOrder || milestonesLoadedFor !== selectedOrder.id) {
      setError('Not saved: this order\'s milestones were never loaded, so saving would overwrite them with blanks. Close and open the tracker again.');
      return;
    }
    setSaving(true); setError('');
    try {
      const allTypes = ['fit_sample','lab_dip','pp_sample','fpt','gpt','yarn','knitting','dyeing','finishing'];
      for (const milestone_type of allTypes) {
        const data = milestoneData[milestone_type] || {};
        await api.patch(`/orders/${selectedOrder.id}/milestone`, {
          milestone_type,
          planned_date: data.planned_date || null,
          actual_date: data.actual_date || null,
          status: data.status || 'pending'
        });
      }
      setShowMilestone(false);
      fetchData();
    } catch (err) {
      setError('Failed to save milestones');
    } finally { setSaving(false); }
  };

  const openMilestone = async (order) => {
    setShowMilestone(false); setMilestonesLoadedFor(null); setMilestoneLoadErr(''); setError('');
    setSelectedOrder(order);
    try {
      const res = await api.get(`/orders/${order.id}`);
      const ms = {};
      (res.data.data.milestones || []).forEach(m => {
        ms[m.milestone_type] = {
          planned_date: m.planned_date ? m.planned_date.split('T')[0] : '',
          actual_date: m.actual_date ? m.actual_date.split('T')[0] : '',
          status: m.status || 'pending'
        };
      });
      setMilestoneData(ms);
      setMilestonesLoadedFor(order.id);
      setShowMilestone(true);
    } catch (err) {
      // NEVER open a blank tracker on a failed read: it looks like an order with no milestones, and
      // Save would write that over the real ones.
      setMilestoneData({});
      const why = err.response?.data?.error || (err.response ? `the server answered ${err.response.status}` : 'no answer from the server');
      setMilestoneLoadErr(`Could not load the milestones for GPO ${order.gpo_no || order.order_no} (${why}). The tracker was not opened, so nothing could be saved over them. Try again.`);
    }
  };

  const statusColor = (status) => {
    const map = {
      done: { bg: '#EEEDF8', color: '#2A2970' },
      approved: { bg: '#EEEDF8', color: '#2A2970' },
      pending: { bg: '#F1EFE8', color: '#5F5E5A' },
      submitted: { bg: '#EEEDF8', color: '#2A2970' },
      rejected: { bg: '#FCEBEB', color: '#A32D2D' },
      in_progress: { bg: '#FAEEDA', color: '#854F0B' },
    };
    return map[status] || map.pending;
  };

  const orderStatusColor = (status) => {
    const map = {
      shipped: { bg: '#EEEDF8', color: '#2A2970' },
      in_production: { bg: '#FAEEDA', color: '#854F0B' },
      confirmed: { bg: '#EEEDF8', color: '#2A2970' },
      complete: { bg: '#EEEDF8', color: '#2A2970' },
      cancelled: { bg: '#FCEBEB', color: '#A32D2D' },
      sample: { bg: '#F1EFE8', color: '#5F5E5A' },
    };
    return map[status] || map.sample;
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', border: '1px solid #ddd',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 500, color: '#666',
    display: 'block', marginBottom: 3
  };

  // ── GPO ↔ OE linkage: derive each order's pipeline from real data ──────────
  const gpoSet = new Set(oeGpos.map(g => g.gpo_no));
  const frsGpoSet = new Set(oeFrs.map(f => f.gpo_no).filter(Boolean));
  const poGpoSet = new Set(oePos.map(p => p.gpo_no).filter(Boolean));
  const estStyles = new Set(oeEstimates.map(e => e.style_no).filter(Boolean));
  const gpoExists = (no) => !!no && gpoSet.has(no);

  const filtered = orders.filter(o => {
    const matchSearch = !search ||
      o.order_no?.toLowerCase().includes(search.toLowerCase()) ||
      o.gpo_no?.toLowerCase().includes(search.toLowerCase()) ||
      o.buyer_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.style_no?.toLowerCase().includes(search.toLowerCase()) ||
      o.colour?.toLowerCase().includes(search.toLowerCase()) ||
      o.current_status?.toLowerCase().includes(search.toLowerCase());
    const matchBuyer = !filterBuyer || o.buyer_name === filterBuyer;
    const matchStatus = !filterStatus || o.status === filterStatus;
    // Default view: only orders with a real GPO in Khyati Portal. "Show all" reveals the rest.
    const matchGpo = showAll || gpoExists(o.gpo_no);
    return matchSearch && matchBuyer && matchStatus && matchGpo;
  });
  const hiddenCount = orders.filter(o => !gpoExists(o.gpo_no)).length;
  const derivePipeline = (order) => {
    const gpoDone = gpoExists(order.gpo_no);
    const ceDone = gpoDone || estStyles.has(order.style_no);
    const frsDone = !!order.gpo_no && frsGpoSet.has(order.gpo_no);
    const poDone = !!order.gpo_no && poGpoSet.has(order.gpo_no);
    const prodDone = ['in_production', 'shipped', 'complete'].includes(order.status);
    const shipDone = ['shipped', 'complete'].includes(order.status);
    return [
      { key: 'CE', done: ceDone }, { key: 'GPO', done: gpoDone }, { key: 'FRS', done: frsDone },
      { key: 'PO', done: poDone }, { key: 'Prod', done: prodDone }, { key: 'Ship', done: shipDone },
    ];
  };
  const Pipeline = ({ order }) => {
    const steps = derivePipeline(order);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <span title={s.key + (s.done ? ' ✓' : ' pending')} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 30 }}>
              <span style={{ fontSize: 11 }}>{s.done ? '✅' : '⚪'}</span>
              <span style={{ fontSize: 8, color: s.done ? '#166534' : '#bbb', fontWeight: s.done ? 700 : 400 }}>{s.key}</span>
            </span>
            {i < steps.length - 1 && <span style={{ color: steps[i + 1].done ? '#16a34a' : '#ddd', fontSize: 10 }}>→</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f8' }}>
      <p style={{ color: '#666' }}>Loading Merchandising...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>

      

      <div style={{ padding: '16px 24px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total Orders', value: orders.length, color: '#3B3A8F' },
            { label: 'In Production', value: orders.filter(o => o.status === 'in_production').length, color: '#BA7517' },
            { label: 'Dispatched', value: orders.filter(o => o.status === 'shipped').length, color: '#E22D2D' },
            { label: 'Buyers', value: buyers.length, color: '#3B3A8F' },
          ].map((k, i) => <KpiCard key={i} label={k.label} value={k.value} color={k.color} />)}
        </div>

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>

          {/* Orders / Sales Plan tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '0 12px' }}>
            {[['orders', '📦 Orders'], ['bulletin', '📋 Sample Bulletin'], ['plans', `📈 Sales Plan${salesPlans.length ? ` (${salesPlans.length})` : ''}`], ['tna', '📅 T&A'], ['overdue', '⏱ Overdue Orders']].map(([k, l]) => (
              <button key={k} onClick={() => setMTab(k)} style={{ padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: mTab === k ? 700 : 400, color: mTab === k ? '#3B3A8F' : '#888', borderBottom: mTab === k ? '2px solid #3B3A8F' : '2px solid transparent' }}>{l}</button>
            ))}
          </div>

          {mTab === 'bulletin' && <SampleBulletinTab user={user} />}
          {mTab === 'plans' && <div style={{ padding: 16 }}><SalesPlanTab plans={salesPlans} onChanged={loadPlans} /></div>}
          {mTab === 'tna' && <TNAContent />}
          {mTab === 'overdue' && <OverdueOrdersTab />}

          {mTab === 'orders' && milestoneLoadErr && (
            <div style={{ margin: '12px 16px 0', background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ flex: 1 }}>{milestoneLoadErr}</span>
              <button onClick={() => setMilestoneLoadErr('')} style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 13 }}>Dismiss</button>
            </div>
          )}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: mTab === 'orders' ? 'flex' : 'none', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Search GPO, buyer, style, colour, status..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, width: 280, padding: '6px 10px' }}
            />
            <select value={filterBuyer} onChange={e => setFilterBuyer(e.target.value)} style={{ ...inputStyle, width: 160, padding: '6px 10px' }}>
              <option value="">All buyers</option>
              {buyers.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: 140, padding: '6px 10px' }}>
              <option value="">All statuses</option>
              <option value="sample">Sample</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_production">In Production</option>
              <option value="shipped">Shipped</option>
              <option value="complete">Complete</option>
            </select>
            <button onClick={() => setShowAll(v => !v)}
              title={showAll ? 'Showing all orders (incl. those without a real GPO)' : `${hiddenCount} order(s) without a real GPO are hidden`}
              style={{ padding: '6px 12px', border: '1px solid ' + (showAll ? '#3B3A8F' : '#ddd'), background: showAll ? '#EEEDF8' : 'white', color: showAll ? '#3B3A8F' : '#666', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: showAll ? 600 : 400, whiteSpace: 'nowrap' }}>
              {showAll ? '✓ Showing all' : `Show all (${hiddenCount} hidden)`}
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={() => { setForm({}); setError(''); setShowAddBuyer(true); }}
                style={{ padding: '6px 14px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                + Buyer
              </button>
              <button onClick={() => navigate('/cost-estimation')}
                title="Starts a new order from Cost Estimation (CE → GPO → FRS → PO)"
                style={{ padding: '6px 16px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                + New Order
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', display: mTab === 'orders' ? 'block' : 'none' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F7F6F3' }}>
                  {['GPO', 'Pipeline', 'Buyer', 'Brand', 'Garment', 'Style', 'Colour', 'Size Ratio', 'Qty', 'Delivery', 'Sales Plan', 'Current Status', 'Order Status', 'Milestones', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#888', fontWeight: 500, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={15} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>
                    {!showAll && hiddenCount > 0
                      ? <>No orders with a real GPO yet. <button onClick={() => setShowAll(true)} style={{ background: 'none', border: 'none', color: '#3B3A8F', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Show all {hiddenCount} order(s)</button> or start one via + New Order.</>
                      : 'No orders found. Click + New Order to add one.'}
                  </td></tr>
                ) : filtered.map((order, i) => {
                  const s = orderStatusColor(order.status);
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 ? '#FAFAFA' : '#FFFFFF' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EEEDF8'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#FAFAFA' : '#FFFFFF'}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {gpoExists(order.gpo_no)
                          ? <span onClick={() => navigate('/order-execution')} title="Open in Order Execution" style={{ color: '#3B3A8F', cursor: 'pointer', textDecoration: 'underline' }}>{order.gpo_no} ↗</span>
                          : <span style={{ color: order.gpo_no ? '#E22D2D' : '#999' }} title={order.gpo_no ? 'No matching GPO in Order Execution yet' : 'No GPO'}>{order.gpo_no || order.order_no}</span>}
                        {riskFlags[order.gpo_no] && <span title={`${riskFlags[order.gpo_no].ra_number||'Risk'} — ${riskFlags[order.gpo_no].pending_signoffs>0?riskFlags[order.gpo_no].pending_signoffs+' sign-off(s) pending':'open risk'}`} style={{ fontSize: 9, background: '#FDE2E2', color: '#B91C1C', padding: '1px 6px', borderRadius: 4, marginLeft: 6, fontWeight: 700 }}>⚠ RISK{riskFlags[order.gpo_no].pending_signoffs>0?' · UNSIGNED':''}</span>}
                      </td>
                      <td style={{ padding: '8px 10px' }}><Pipeline order={order} /></td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{order.buyer_name}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{order.brand || '—'}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{order.category || order.style_no}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{order.style_no}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {order.colour ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ddd', display: 'inline-block', border: '1px solid #ccc' }}></span>
                            {order.colour}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: '#666', whiteSpace: 'nowrap' }}>{order.size_ratio || '—'}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{parseInt(order.quantity).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#666' }}>
                        {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <SalesPlanButton order={order} plan={planByGpo[order.gpo_no || order.order_no]} onClick={() => setPlanOrder(order)} />
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 160 }}>
                        <input
                          defaultValue={order.current_status || ''}
                          onBlur={e => {
                            if (e.target.value !== order.current_status) {
                              handleUpdateStatus(order.id, e.target.value);
                            }
                          }}
                          placeholder="Enter status..."
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid #eee', borderRadius: 4, fontSize: 12, background: 'transparent' }}
                        />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <select
                          value={order.status}
                          onChange={e => api.patch(`/orders/${order.id}/status`, { status: e.target.value }).then(fetchData).catch(reportError('change the order status', fetchData))}
                          style={{ padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: 11, background: s.bg, color: s.color, cursor: 'pointer' }}
                        >
                          <option value="sample">Sample</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="in_production">In Production</option>
                          <option value="shipped">Shipped</option>
                          <option value="complete">Complete</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <button onClick={() => openMilestone(order)}
                          style={{ padding: '3px 8px', background: '#EEEDFE', color: '#3C3489', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          View / Edit
                        </button>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <button onClick={() => { setSelectedOrder(order); setForm({ current_status: order.current_status, colour: order.colour, size_ratio: order.size_ratio }); setShowEditOrder(true); }}
                          style={{ padding: '3px 8px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {mTab === 'orders' && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #eee', fontSize: 12, color: '#888' }}>
              Showing {filtered.length} of {orders.length} orders
            </div>
          )}
        </div>
      </div>


      {planOrder && (
        <SalesPlanModal order={planOrder} plan={planByGpo[planOrder.gpo_no || planOrder.order_no]}
          costPerPc={costPerPcFor(planOrder)} user={user}
          onClose={() => setPlanOrder(null)} onSaved={() => { setPlanOrder(null); loadPlans(); }} />
      )}
      {showAddBuyer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 420 }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 20px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>New Buyer</h3></div>
            {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleAddBuyer}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Buyer Name *</label>
                  <input style={inputStyle} placeholder="Easy Buy" required onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input style={inputStyle} type="email" onChange={e => setForm({ ...form, contact_email: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Phone</label>
                  <input style={inputStyle} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Payment Terms</label>
                  <input style={inputStyle} placeholder="Net 30" onChange={e => setForm({ ...form, payment_terms: e.target.value })} />
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} placeholder="Shop / Street / Area" onChange={e => setForm({ ...form, address: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>GST Number</label>
                  <input style={inputStyle} placeholder="27AAAAA0000A1Z5" onChange={e => setForm({ ...form, gstin: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Billing Address</label>
                  <input style={inputStyle} placeholder="Billing address" onChange={e => setForm({ ...form, billing_address: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Delivery Address</label>
                  <input style={inputStyle} placeholder="Delivery address" onChange={e => setForm({ ...form, delivery_address: e.target.value })} />
                </div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Address</label>
                  <input style={inputStyle} placeholder="Shop / Street / Area" onChange={e => setForm({ ...form, address: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>GST Number</label>
                  <input style={inputStyle} placeholder="27AAAAA0000A1Z5" onChange={e => setForm({ ...form, gstin: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Billing Address</label>
                  <input style={inputStyle} placeholder="Billing address" onChange={e => setForm({ ...form, billing_address: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Delivery Address</label>
                  <input style={inputStyle} placeholder="Delivery address" onChange={e => setForm({ ...form, delivery_address: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddBuyer(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Add Buyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMilestone && selectedOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, background: '#2D2C72', margin: '-28px -28px 20px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Milestone Tracker</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.72)' }}>
                  GPO: {selectedOrder.gpo_no || selectedOrder.order_no} — {selectedOrder.buyer_name} — {selectedOrder.colour}
                </p>
              </div>
              <button onClick={() => setShowMilestone(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'rgba(255,255,255,0.85)' }}>×</button>
            </div>
            {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSaveMilestone}>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#3B3A8F', marginBottom: 8, padding: '4px 8px', background: '#EEEDF8', borderRadius: 4, display: 'inline-block' }}>
                  BUYER PROCESS
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F7F6F3' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee', width: 120 }}>Milestone</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>Planned Date</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>Actual Date</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BUYER_MILESTONES.map(type => {
                      const m = milestoneData[type] || {};
                      const sc = statusColor(m.status);
                      return (
                        <tr key={type} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '7px 10px', fontWeight: 500 }}>{MILESTONE_LABELS[type]}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <input type="date" value={m.planned_date || ''} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], planned_date: e.target.value } }))} />
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <input type="date" value={m.actual_date || ''} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], actual_date: e.target.value } }))} />
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <select value={m.status || 'pending'} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12, background: sc.bg, color: sc.color }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], status: e.target.value } }))}>
                              <option value="pending">Pending</option>
                              <option value="submitted">Submitted</option>
                              <option value="approved">Approved / Done</option>
                              <option value="rejected">Rejected</option>
                              <option value="in_progress">In Progress</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#085041', marginBottom: 8, padding: '4px 8px', background: '#E1F5EE', borderRadius: 4, display: 'inline-block' }}>
                  FABRIC PROCESS
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F7F6F3' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee', width: 120 }}>Stage</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>Start Date</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>End Date</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FABRIC_MILESTONES.map(type => {
                      const m = milestoneData[type] || {};
                      const sc = statusColor(m.status);
                      return (
                        <tr key={type} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '7px 10px', fontWeight: 500 }}>{MILESTONE_LABELS[type]}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <input type="date" value={m.planned_date || ''} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], planned_date: e.target.value } }))} />
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <input type="date" value={m.actual_date || ''} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], actual_date: e.target.value } }))} />
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <select value={m.status || 'pending'} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12, background: sc.bg, color: sc.color }}
                              onChange={e => setMilestoneData(prev => ({ ...prev, [type]: { ...prev[type], status: e.target.value } }))}>
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="approved">Done</option>
                              <option value="rejected">Issue</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowMilestone(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Milestones'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditOrder && selectedOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 440 }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 20px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Edit Order — {selectedOrder.gpo_no || selectedOrder.order_no}</h3></div>
            <form onSubmit={async (e) => {
              e.preventDefault(); setSaving(true);
              try {
                await api.patch(`/orders/${selectedOrder.id}`, form);
                setShowEditOrder(false); fetchData();
              } catch (err) { console.error(err); }
              finally { setSaving(false); }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Colour</label>
                  <input style={inputStyle} defaultValue={selectedOrder.colour} onChange={e => setForm({ ...form, colour: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Size Ratio</label>
                  <input style={inputStyle} defaultValue={selectedOrder.size_ratio} onChange={e => setForm({ ...form, size_ratio: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>GPO No.</label>
                  <input style={inputStyle} defaultValue={selectedOrder.gpo_no} onChange={e => setForm({ ...form, gpo_no: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Delivery Date</label>
                  <input style={inputStyle} type="date" defaultValue={selectedOrder.delivery_date?.split('T')[0]} onChange={e => setForm({ ...form, delivery_date: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Current Status</label>
                  <input style={inputStyle} defaultValue={selectedOrder.current_status} onChange={e => setForm({ ...form, current_status: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Photo / Drive URL</label>
                  <input style={inputStyle} defaultValue={selectedOrder.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditOrder(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}